import { Bell, BriefcaseBusiness, MessageCircleWarning, MessageSquareWarning, Truck } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageProvider';
import './cabinet-empty-state.css';

type EmptyKind = 'messages' | 'notifications' | 'offers' | 'requests' | 'transports' | 'search';

const COPY = {
  messages: ['Здесь появятся ваши чаты', 'Сообщения по заявкам и сделкам будут собраны в одном месте.', 'Your chats will appear here', 'Messages about requests and deals will be collected in one place.'],
  notifications: ['Пока без уведомлений', 'Когда по вашим заявкам или откликам появятся изменения, мы сообщим об этом здесь.', 'No notifications yet', 'Updates to your requests or offers will appear here.'],
  offers: ['У вас пока нет откликов', 'После отклика на заявку вы сможете отслеживать, принят он, ожидает ответа или отклонён.', 'You have no offers yet', 'After responding to a request, you can track whether your offer is accepted, pending or declined.'],
  requests: ['У вас пока нет заявок', 'Создайте заявку на перевозку, и она появится здесь.', 'You have no requests yet', 'Create a transport request and it will appear here.'],
  transports: ['Сейчас у вас нет активных перевозок', 'Здесь появится информация о вашей поездке после принятия отклика.', 'You have no active transports', 'Your trip details will appear here after an offer is accepted.'],
  search: ['Заявки не найдены', 'Попробуйте изменить фильтры или проверьте другие категории.', 'No requests found', 'Try changing the filters or checking another category.'],
} as const;

const ICONS = { messages: MessageCircleWarning, notifications: Bell, offers: MessageSquareWarning, requests: BriefcaseBusiness, transports: Truck, search: BriefcaseBusiness };

export function CabinetEmptyState({ kind, filtered = false, title, description, role }: { kind: EmptyKind; filtered?: boolean; title?: string; description?: string; role?: 'carrier' | 'customer' }) {
  const { language } = useLanguage();
  const Icon = ICONS[kind];
  const copy = COPY[kind];
  const offset = language === 'en' ? 2 : 0;
  const notificationDescription = kind === 'notifications' && role
    ? role === 'carrier'
      ? (language === 'en' ? 'Updates to your offers will appear here.' : 'Когда по вашим откликам появятся изменения, мы сообщим об этом здесь.')
      : (language === 'en' ? 'Updates to your requests will appear here.' : 'Когда по вашим заявкам появятся изменения, мы сообщим об этом здесь.')
    : undefined;
  const filteredTitle = language === 'en'
    ? (kind === 'offers' ? 'No offers in this category' : 'No requests in this category')
    : (kind === 'offers' ? 'В этой категории пока нет откликов' : 'В этой категории пока нет заявок');
  return (
    <section className="cabinet-empty-state" role="status" data-empty-kind={kind} data-i18n-skip>
      <Icon aria-hidden="true" size={24} strokeWidth={1.5} />
      <strong>{title ?? (filtered ? filteredTitle : copy[offset])}</strong>
      <p>{description ?? notificationDescription ?? (filtered ? (language === 'en' ? 'Choose another category to see your entries.' : 'Выберите другую категорию, чтобы увидеть ваши записи.') : copy[offset + 1])}</p>
    </section>
  );
}

export function CabinetLoadState({ state }: { state: string }) {
  const { language } = useLanguage();
  if (state !== 'loading' && state !== 'error') return null;
  return <p className="cabinet-load-state" role={state === 'error' ? 'alert' : 'status'} data-i18n-skip>{state === 'loading'
    ? (language === 'en' ? 'Loading…' : 'Загружаем…')
    : (language === 'en' ? 'Could not load the data. Please refresh the page.' : 'Не удалось загрузить данные. Обновите страницу.')}</p>;
}
